/**
 * In-memory employment reference catalog state and pure readers.
 *
 * Kept free of API/axios imports so plain-Node fixture tests can import the
 * readers (see employeeWizardUx.fixture.test.js). Network access lives in
 * useEmploymentReferenceCatalog.js.
 */

import { ref } from 'vue';
import { EMPLOYMENT_TYPE_OPTIONS, COMPANY_OPTIONS } from '../constants/employmentOptions.js';

/** @type {import('vue').Ref<{ employment_types: Array, companies: Array }|null>} */
export const snapshotRef = ref(null);
export const loadingRef = ref(false);
export const errorRef = ref('');
let requestVersion = 0;

/** Shared across all forms; reset also invalidates any pending snapshot write. */
export function beginEmploymentReferenceCatalogLoad() {
  const version = ++requestVersion;
  return () => version === requestVersion;
}

/** Clear in-memory catalog so employee forms reload on next ensureLoaded(). */
export function resetEmploymentReferenceCatalogSnapshot() {
  requestVersion += 1;
  snapshotRef.value = null;
  loadingRef.value = false;
  errorRef.value = '';
}

export function fallbackSnapshot() {
  return {
    employment_types: EMPLOYMENT_TYPE_OPTIONS.map((row, index) => ({
      id: null,
      code: row.value,
      label: row.label,
      value: row.value,
      is_active: true,
      is_system: true,
      sort_order: index * 10,
    })),
    companies: COMPANY_OPTIONS.map((row, index) => ({
      id: null,
      name: row.value,
      label: row.label,
      value: row.value,
      employment_type_codes: [...(row.employmentTypes || [])],
      is_active: true,
      is_system: true,
      sort_order: index * 10,
    })),
  };
}

export function normalizeCode(value) {
  return String(value ?? '').trim();
}

export function findTypeByCode(code) {
  const key = normalizeCode(code);
  if (!key || !snapshotRef.value) return null;
  return (snapshotRef.value.employment_types || []).find((row) => row.code === key) || null;
}

export function findCompanyByName(name) {
  const key = normalizeCode(name);
  if (!key || !snapshotRef.value) return null;
  return (snapshotRef.value.companies || []).find((row) => row.name === key) || null;
}

export function mapTypeOption(row) {
  return {
    id: row.id ?? null,
    label: row.label || row.code,
    value: row.code,
    code: row.code,
    is_system: !!row.is_system,
  };
}

export function mapCompanyOption(row) {
  return {
    id: row.id ?? null,
    label: row.name,
    value: row.name,
    name: row.name,
    employment_type_codes: row.employment_type_codes || [],
    is_system: !!row.is_system,
  };
}

export function getCatalogEmploymentTypeOptions() {
  const rows = snapshotRef.value?.employment_types || fallbackSnapshot().employment_types;
  return rows.filter((row) => row.is_active !== false).map(mapTypeOption);
}

export function getCatalogCompanyOptionsForEmploymentType(type) {
  const typeCode = normalizeCode(type);
  if (!typeCode) return [];
  const rows = snapshotRef.value?.companies || fallbackSnapshot().companies;
  return rows
    .filter(
      (row) =>
        row.is_active !== false &&
        (row.employment_type_codes || []).includes(typeCode)
    )
    .map(mapCompanyOption);
}

/** All active employing companies (for legacy employment types such as full_time). */
export function getAllCatalogCompanySelectOptions() {
  const rows = snapshotRef.value?.companies || fallbackSnapshot().companies;
  const seen = new Set();
  const options = [];
  for (const row of rows) {
    if (row.is_active === false) continue;
    const name = normalizeCode(row.name || row.value);
    if (!name || seen.has(name)) continue;
    seen.add(name);
    options.push({ label: name, value: name });
  }
  if (options.length) return options;
  return COMPANY_OPTIONS.map(({ label, value }) => ({ label, value }));
}
